import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { MasterContext } from './MasterContext';

import { Providers, SelectOption } from '../../interfaces';

import { useCountryStateCity } from '../../hooks';
import { useBranch } from '../../hooks/Branch';

import MasterService from '../../services/master';
import { QuotationGroups } from '../../../pages/quotation/NewQuote/containers';
import { GlobalPublicConfigs } from '../../interfaces/services/global-config-service';

export const MasterProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    isLoadingProvinceList,
    getCountriesList,
    getProvinceList,
    getLocalitiesPerProvince,
  } = useCountryStateCity();
 
  const {
    getBranchList,
    isLoadingBranchList,
  } = useBranch();

  const [countryList, setCountryList] = useState<Array<SelectOption> | undefined>([]);

  const [provinceList, setProvinceList] = useState<Array<SelectOption> | undefined>([]);

  const [localities, setLocalities] = useState<Array<SelectOption>>();
  const [allLocalitiesPerProvince, setAllLocalitiesPerProvince] = useState<
    Array<{ provinceCode: string; localitiesOptions: Array<SelectOption> }>
  >([]);
  const [isLoadingLocalities, setIsLoadingLocalities] =
    useState<boolean>(false);
                  
  const [branchList, setbranchList] = useState< Array<SelectOption> | undefined >([]);

  const [ documentTypeList, setDocumentTypeList ] = useState<Array<SelectOption>>();
  const [ isLoadingDocumentTypeList, setIsLoadingDocumentTypeList ] = useState<boolean>(false);

  const [ genderList, setGenderList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingGenderList, setIsLoadingGenderList ] = useState<boolean>(true);
  
  const [ civilStatusList, setCivilStatusList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingCivilStatusList, setIsLoadingCivilStatusList ] = useState<boolean>(true);
  
  const [ accidentTypeList, setAccidentTypeList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingAccidentTypeList, setIsLoadingAccidentTypeList ] = useState<boolean>(true);

  const [ collisionWithList, setCollisionWithList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingCollisionWithList, setIsLoadingCollisionWithList ] = useState<boolean>(true);

  const [ providersList, setProvidersList ] = useState<Array<Providers>>([]);
  const [ isLoadingProvidersList, setIsLoadingProvidersList ] = useState<boolean>(true);

  const [ vehicleTypesList, setVehicleTypesList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingVehicleTypesList, setIsLoadingVehicleTypesList ] = useState<boolean>(true);

  const [ useList, setUseList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingUseList, setIsLoadingUseList ] = useState<boolean>(true);

  const [ yearList, setYearList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingYearList, setIsLoadingYearList ] = useState<boolean>(true);

  const [ vehicleBrandList, setVehicleBrandList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingVehicleBrandList, setIsLoadingVehicleBrandList ] = useState<boolean>(true);

  const [ vehicleModelsPerBrandList, setVehicleModelsPerBrandList ] = useState<Array<SelectOption>>([]);
  const [ isLoadingVehicleModelsPerBrandList, setIsLoadingVehicleModelsPerBrandList ] = useState<boolean>(true);

  const [savedQuotationInfo, setSavedQuotationInfo] = useState<{ quotationNumber: string, quotationGroup: QuotationGroups }>();

  const [globalPublicConfigs, setGlobalPublicConfigs] = useState<GlobalPublicConfigs>();

  const getCountryList = async () => {
    const countriesData = await getCountriesList();
    setCountryList(countriesData);
  };

  const getProvincesList = async () => {
    const provincesData = await getProvinceList();
    setProvinceList(provincesData);
  };

  const getLocalitiesByProvinceFromState = useCallback(
    (provinceCode: string) =>
      allLocalitiesPerProvince.find(
        (el) => String(el.provinceCode) === String(provinceCode)
      )?.localitiesOptions,
    [allLocalitiesPerProvince]
  );
   
  const getBranchsList = async () => {
    const branchList = await getBranchList();
    
    setbranchList(branchList);
  };

  const getLocalitiesList = useCallback(
    async (provinceCode: string) => {
      try {
        setIsLoadingLocalities(true);

        if (
          allLocalitiesPerProvince.find(
            (el) => String(el.provinceCode) === String(provinceCode)
          )
        )
          return;

        const listLocalities = await getLocalitiesPerProvince(provinceCode);

        if (listLocalities?.length) {
          setLocalities(listLocalities);

          if (
            !allLocalitiesPerProvince.find(
              (el) => String(el.provinceCode) === String(provinceCode)
            )
          ) {
            setAllLocalitiesPerProvince((prevValues) => [
              ...prevValues,
              { provinceCode, localitiesOptions: listLocalities },
            ]);
          }
        }
      } catch (error) {
      } finally {
        setIsLoadingLocalities(false);
      }
    },
    [allLocalitiesPerProvince]
  );

  const getDocumentTypeList = useCallback(async () => {
    try {
      setIsLoadingDocumentTypeList(true);
      const response = await MasterService.getDocumentTypeList();

      if ( response ) {
        setDocumentTypeList( response.data );
      }
    } catch (error) {
    } finally {
      setIsLoadingDocumentTypeList(false);
    }
  }, [ setIsLoadingDocumentTypeList, setDocumentTypeList ])

  const getGenderList = useCallback(async () => {
    try {
      const response = await MasterService.getGenderList();
  
      if (response) {
        setGenderList(response.data);
        setIsLoadingGenderList(false);
      }
    } catch (error) {}
  }, [ setGenderList, setIsLoadingGenderList ]);

  const getCivilStatusList = useCallback(async () => {
    try {
      const response = await MasterService.getListCivilStatus();

      if (response) {

        setCivilStatusList(response.data);
        setIsLoadingCivilStatusList(false)
      }
    } catch (error) {}
  }, [ setCivilStatusList ]);

  const getAccidentTypeList = useCallback(async () => {
    try {
      const response = await MasterService.getAccidentTypeList();

      if (response) {
        setAccidentTypeList(response.data);
        setIsLoadingAccidentTypeList(false);
      }
    } catch (error) {}
  }, [ setAccidentTypeList, setIsLoadingAccidentTypeList ]);

  const getCollisionWithList = useCallback(async () => {
    try {
      const response = await MasterService.getCollisionWithList();

      if (response) {
        setCollisionWithList(response.data);
        setIsLoadingCollisionWithList(false);
      }
    } catch (error) {}
  }, [ setCollisionWithList, setIsLoadingCollisionWithList ]);

  const getProvidersList = useCallback(async (param:any) => {
    try {
      const response = await MasterService.getProvidersList(param);
      if (response.data) {
        setProvidersList(response.data.proveedores);
        setIsLoadingProvidersList(false);
      }
    } catch (error) {}
  }, [ setProvidersList, setIsLoadingProvidersList ]);

  const getVehicleTypesList = useCallback(async () => {
    try {
      const response = await MasterService.getVehicleTypesList();
      if (response) {
        setVehicleTypesList(response.data);
        setIsLoadingVehicleTypesList(false);
      }
    } catch (error) {}
  }, [ setVehicleTypesList, setIsLoadingVehicleTypesList ]);

  const getUseList = useCallback(async () => {
    try {
      const response = await MasterService.getUseList();

      if (response) {
        setUseList(response.data);
        setIsLoadingUseList(false);
      }
    } catch (error) {}
  }, [ setUseList, setIsLoadingUseList ]);

  const getYearList = useCallback(async () => {
    try {
      const response = await MasterService.getYearList();

      if (response) {
        setYearList(response.data);
        setIsLoadingYearList(false);
      }
    } catch (error) {}
  }, [ setYearList, setIsLoadingYearList ]);

  const getVehicleBrandsList = useCallback(async (rama: string) => {
    try {
      const response = await MasterService.getVehicleBrandsList(rama);

      if (response) {
        setVehicleBrandList(response.data);
        setIsLoadingVehicleBrandList(false);
      }
    } catch (error) {}
  }, [ setVehicleBrandList, setIsLoadingVehicleBrandList ]);

  const getVehicleModelsPerBrandList = useCallback(async (codigoMarcaInfoauto: string) => {
    try {
      const response = await MasterService.getVehicleModelsPerBrandList(codigoMarcaInfoauto);

      if (response) {
        setVehicleModelsPerBrandList(response.data);
        setIsLoadingVehicleModelsPerBrandList(false);
      }
    } catch (error) {}
  }, [ setVehicleModelsPerBrandList, setIsLoadingVehicleModelsPerBrandList ]);

  const getGlobalConfigs = useCallback(async () => {
    try {
      const response = await MasterService.getGlobalConfigs();
      if (response) {        
        setGlobalPublicConfigs(response.data)
      }
    } catch (error) {}
  }, [ setGlobalPublicConfigs ])

  useEffect(() => {
    if(!globalPublicConfigs){
      getGlobalConfigs()
    }
  }, []) 

  const values = useMemo(
    () => ({
      getProvincesList,
      provinceList,
      isLoadingProvinceList,

      getLocalitiesByProvinceFromState,
      getLocalitiesList,
      isLoadingLocalities,
      allLocalitiesPerProvince,

      getDocumentTypeList,
      documentTypeList,
      isLoadingDocumentTypeList,
      
      getGenderList,
      genderList,
      isLoadingGenderList,
      
      getBranchsList,
      branchList,
      isLoadingBranchList,

      getCivilStatusList,
      civilStatusList,
      isLoadingCivilStatusList,

      getAccidentTypeList,
      accidentTypeList,
      isLoadingAccidentTypeList,

      getCollisionWithList,
      collisionWithList,
      isLoadingCollisionWithList,
      
      getProvidersList,
      providersList,
      isLoadingProvidersList,

      getVehicleTypesList,
      vehicleTypesList,
      isLoadingVehicleTypesList,

      getUseList,
      useList,
      isLoadingUseList,

      getYearList,
      yearList,
      isLoadingYearList,

      getVehicleBrandsList,
      vehicleBrandList,
      isLoadingVehicleBrandList,
      setVehicleModelsPerBrandList,

      getVehicleModelsPerBrandList,
      vehicleModelsPerBrandList,
      isLoadingVehicleModelsPerBrandList,
      
      getCountryList,
      countryList,
      setProvinceList,
      setLocalities,

      savedQuotationInfo,
      setSavedQuotationInfo,

      globalPublicConfigs, 
      setGlobalPublicConfigs
    }),
    [
      getProvincesList,
      provinceList,
      isLoadingProvinceList,

      getLocalitiesByProvinceFromState,
      getLocalitiesList,
      isLoadingLocalities,
      allLocalitiesPerProvince,

      getDocumentTypeList,
      documentTypeList,
      isLoadingDocumentTypeList,
      
      getGenderList,
      genderList,
      isLoadingGenderList,
      
      getBranchsList,
      branchList,
      isLoadingBranchList,

      getCivilStatusList,
      civilStatusList,
      isLoadingCivilStatusList,

      getAccidentTypeList,
      accidentTypeList,
      isLoadingAccidentTypeList,

      getCollisionWithList,
      collisionWithList,
      isLoadingCollisionWithList,
      
      getProvidersList,
      providersList,
      isLoadingProvidersList,

      getVehicleTypesList,
      vehicleTypesList,
      isLoadingVehicleTypesList,

      getUseList,
      useList,
      isLoadingUseList,

      getYearList,
      yearList,
      isLoadingYearList,

      getVehicleBrandsList,
      vehicleBrandList,
      isLoadingVehicleBrandList,
      setVehicleModelsPerBrandList,
      setVehicleModelsPerBrandList,

      getVehicleModelsPerBrandList,
      vehicleModelsPerBrandList,
      isLoadingVehicleModelsPerBrandList,

      getCountryList,
      countryList,
      setProvinceList,
      setLocalities,

      savedQuotationInfo,
      setSavedQuotationInfo,

      globalPublicConfigs, 
      setGlobalPublicConfigs
    ]
  );

  return (
    <MasterContext.Provider value={values}>{children}</MasterContext.Provider>
  );
};
